import { useRouter } from "next/router";
import React from "react";
import Breadcrumbs from "@/utils/Breadcrumbs";
import SocialCounter from "./SocialCounter";
import { useState, useEffect } from "react";
import Link from "next/link";
import Head from "next/head";
import BreadcrumbSchema from "@/schema/BreadcrumbSchema";

import { GetServerSideProps } from "next";

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug } = context.params!;
  const API_URL = `${process.env.NEXT_PUBLIC_MAIN_API_URL}/b2b/get-all-country`;

  // Check for the presence of "-[slug]" in the slug
  const slugPattern = /-\[slug\]/; // Adjust this pattern as necessary
  if (Array.isArray(slug) ? slug[0].match(slugPattern) : slug?.match(slugPattern)) {
    return {
      redirect: {
        destination: '/stats',
        permanent: false, // Set to true for a permanent redirect (301)
      },
    };
  }

  const toCamelCaseWithSpaces = (str: string) => {
    return str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const countryName = Array.isArray(slug)
    ? toCamelCaseWithSpaces(slug[0].split("-in-")[1])
    : slug
    ? toCamelCaseWithSpaces(slug.split("-in-")[1])
    : "";

  let countryStats = null;
  let error = null;

  try {
    // Fetch all countries
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success) {
      const selectedCountry = data.data.c_l.find(
        (country: Country) => country.n === countryName
      );
      if (selectedCountry) {
        const statsResponse = await fetch(
          `${process.env.SOCIAL_STATS_API_URL}/api?type=general&countryId=${selectedCountry.i}`
        );

        if (!statsResponse.ok) {
          throw new Error(
            `HTTP error while fetching stats! status: ${statsResponse.status}`
          );
        }

        countryStats = await statsResponse.json();
        // console.log(countryStats)
      } else {
        error = "Country not found";
      }
    } else {
      error = "Failed to fetch countries";
    }
  } catch (err: any) {
    error = err.message;
    console.error("Fetch error:", err);
  }

  return {
    props: {
      countryStats,
      error,
    },
  };
};

interface Country {
  s: string;
  i: number;
  n: string;
}

interface CountryStats {
  facebookStats: any;
  instagramStats: any;
  messengerStats: any;
  linkedinStats: any;
}

interface StatsDetailPageProps {
  countryStats: CountryStats | null;
  error: string | null;
}

const StatsDetailPage = ({ countryStats, error }: StatsDetailPageProps) => {
  const router = useRouter();
  const { slug } = router.query;

  const toCamelCaseWithSpaces = (str: string) => {
    return str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const countryName = Array.isArray(slug)
    ? toCamelCaseWithSpaces(slug[0].split("-in-")[1])
    : slug
    ? toCamelCaseWithSpaces(slug.split("-in-")[1])
    : "";

  // Function to generate month list grouped by year
  const generateYearlyMonthList = (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number
  ): Record<number, string[]> => {
    const result: Record<number, string[]> = {};
    let currentYear = startYear;
    let currentMonth = startMonth;

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonth <= endMonth)
    ) {
      if (!result[currentYear]) {
        result[currentYear] = [];
      }

      result[currentYear].push(
        `${currentYear}/${String(currentMonth).padStart(2, "0")}`
      );

      currentMonth += 1;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear += 1;
      }
    }

    return result;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const yearMonthList = generateYearlyMonthList(2018, 9, 2024, 8);
  const lastYear = Math.max(...Object.keys(yearMonthList).map(Number)); // Get the last year
  const [selectedYear, setSelectedYear] = useState<number | null>(lastYear); // Set default to last year

  const location = [
    { text: "Home", url: "/" },
    { text: "Stats", url: "/stats" },
    { text: `${countryName}` },
  ];

  const breadcrumbItems = [
    { name: "Home", url: "https://www.bizdataset.com/" },
    // Add more breadcrumb items as needed
    { name: "Stats", url: "https://www.bizdataset.com/stats" },
    {
      name: `${countryName}`,
      url: `https://www.bizdataset.com/Stats/social-media-users-in-${countryName
        .toLowerCase()
        .replace(/\s+/g, "-")}`,
    },
  ];

  const formatNumberWithSpaces = (number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " "); // Format with spaces
  };
 
  const messages = [
    <>
      <div className="text-center">
        {formatNumberWithSpaces(countryStats?.facebookStats?.total || 0)}{" "}
      </div>
      <span className="gradient-new-text">Facebook users</span> in{" "}
      {countryName || "Country"}
    </>,
    <>
      <div className="text-center">
        {" "}
        {formatNumberWithSpaces(countryStats?.instagramStats?.total || 0)}
      </div>
      <span className="gradient-new-text">Instagram users</span> in{" "}
      {countryName || "Country"}
    </>,
    <>
      <div className="text-center">
        {formatNumberWithSpaces(countryStats?.messengerStats?.total || 0)}
      </div>
      <span className="gradient-new-text">Messenger users</span> in{" "}
      {countryName || "Country"}
    </>,
    <>
      <div className="text-center">
        {formatNumberWithSpaces(countryStats?.linkedinStats?.total || 0)}{" "}
      </div>
      <span className="gradient-new-text">LinkedIn users</span> in{" "}
      {countryName || "Country"}
    </>,
  ];

  const totalRecords = countryStats?.facebookStats?.total ?? 0;
  const totalPhoneNumbers = countryStats?.instagramStats?.total ?? 0;
  const totalEmails = countryStats?.messengerStats?.total ?? 0;
  const totalFacebooks = countryStats?.linkedinStats?.total ?? 0;

  useEffect(() => {
    // Check if all values are 0
    if (totalRecords === 0 && totalPhoneNumbers === 0 && totalEmails === 0 && totalFacebooks === 0) {
      // Redirect to /stats page
      router.push('/stats');
    }
  }, [totalRecords, totalPhoneNumbers, totalEmails, totalFacebooks, router]); // Depend on the stats values and router


  const page1Icons = [
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 320 512"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"></path>
    </svg>,
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 448 512"
      height="30px"
      width="30px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
    </svg>, // Record icon for page 1
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 512 512"
      height="28px"
      width="28px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M256.55 8C116.52 8 8 110.34 8 248.57c0 72.3 29.71 134.78 78.07 177.94 8.35 7.51 6.63 11.86 8.05 58.23A19.92 19.92 0 0 0 122 502.31c52.91-23.3 53.59-25.14 62.56-22.7C337.85 521.8 504 423.7 504 248.57 504 110.34 396.59 8 256.55 8zm149.24 185.13l-73 115.57a37.37 37.37 0 0 1-53.91 9.93l-58.08-43.47a15 15 0 0 0-18 0l-78.37 59.44c-10.46 7.93-24.16-4.6-17.11-15.67l73-115.57a37.36 37.36 0 0 1 53.91-9.93l58.06 43.46a15 15 0 0 0 18 0l78.41-59.38c10.44-7.98 24.14 4.54 17.09 15.62z"></path>
    </svg>,
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 448 512"
      height="30px"
      width="30px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
    </svg>,
  ];

  const monthWithLeadingZero = (monthNumber: { toString: () => string }) => {
    return monthNumber.toString().padStart(2, "0"); // Ensure it's 2 digits, padding with '0' if necessary
  };

  const [searchTerm, setSearchTerm] = useState("");

  const filteredMonths = Object.keys(yearMonthList)
    .sort((a, b) => parseInt(b, 10) - parseInt(a, 10)) // Sort years in descending order
    .flatMap((yearStr) => {
      const year = parseInt(yearStr, 10);

      // Filter months based on the search term
      const months = yearMonthList[year].filter((month) => {
        const monthName = monthNames[parseInt(month.split("/")[1], 10) - 1];
        return (
          monthName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          year.toString().includes(searchTerm)
        );
      });

      // Map months to the desired structure and reverse the order
      return months
        .map((month) => ({
          month,
          year,
        }))
        .reverse(); // Reverse the order of months
    });

  const title = `Social Media Users in ${
    countryName || "Country"
  } By Platform`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url ? { "@id": item.url } : undefined,
    })),
  };

  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <title>{title}</title>

        {/* <BreadcrumbSchema items={breadcrumbItems} /> */}
 {/* Breadcrumb Schema */}
 <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />

        <meta
          property="og:title"
          content={`Social Media Users in ${
            countryName || "Country"
          } By Platform`}
        />
        <meta
          name="twitter:title"
          content={`Social Media Users in ${
            countryName || "Country"
          } By Platform`}
        />

        <meta
          name="description"
          content={`Get social media users in ${
            countryName || "Country"
          } stats for top social media platforms such as Facebook, Instagram, LinkedIn, and Messenger for free. The data is updated monthly and includes age and gender distribution.`}
        />
        <meta
          property="og:description"
          content={`Get social media users in ${
            countryName || "Country"
          } stats for top social media platforms such as Facebook, Instagram, LinkedIn, and Messenger for free. The data is updated monthly and includes age and gender distribution.`}
        />
        <meta
          name="twitter:description"
          content={`Get social media users in ${
            countryName || "Country"
          } stats for top social media platforms such as Facebook, Instagram, LinkedIn, and Messenger for free. The data is updated monthly and includes age and gender distribution.`}
        />

        <meta
          name="keywords"
          content={`Social media users in ${
            countryName || "Country"
          }, Most used social media platform in ${
            countryName || "Country"
          }, Social Media Demographics ${
            countryName || "Country"
          }, Age wise social media users in ${
            countryName || "Country"
          }, Social media users in ${
            countryName || "Country"
          } by Platform, Social media users data in ${
            countryName || "Country"
          }`}
        />

        <link
          rel="canonical"
          href={`https://www.bizdataset.com/stats/social-media-users-in-${countryName
            .toLowerCase()
            .replace(/\s+/g, "-")}`}
        />
        <meta
          property="og:url"
          content={`https://www.bizdataset.com/stats/social-media-users-in-${countryName
            .toLowerCase()
            .replace(/\s+/g, "-")}`}
        />

        <meta
          property="og:image"
          content="https://bizdataset-report.s3.eu-north-1.amazonaws.com/asset/favicon-512x512.png"
        />
        <meta
          name="twitter:image"
          content="https://bizdataset-report.s3.eu-north-1.amazonaws.com/asset/favicon-512x512.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bizdataset" />

        <meta name="robots" content="index, follow"></meta>
        <link rel="dns-prefetch" href={`${process.env.SOCIAL_STATS_API_URL}`} />
      </Head>{" "}
      <section className="relative w-full ">
        {/* Pseudo-element for background image with reduced opacity */}
        {/* <div className="absolute inset-0 mt-14 sm:w-auto md:w-auto lg:w-[1800px] lg:h-[400px] flex items-center justify-center sm:ml-4 md:ml-12 lg:ml-24 background-image-custom"></div> */}
        <div className="absolute inset-0 mt-14 sm:w-auto md:w-auto lg:w-auto flex items-center justify-center sm:ml-4 md:ml-12 lg:ml-24 background-image-custom"></div>

        <div className=" lg:mt-30 mx-2 md:mx-4 lg:mx-0">
          <div className="mb-3 py-9 w-full flex mt-10 items-center justify-center  flex-col relative z-10">
            <Breadcrumbs location={location} />
            <div className="flex items-center uppercase justify-center tracking-wide text-[20px] text-[var(--title-color)] font-semibold  mb-4 md:mb-2">
              Social media Stats
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center  mb-4 tracking-wide  text-[var(--title-color)]">
              Social media users in{" "}
              <span className="gradient-new-text">
                {countryName || "Country"}
              </span>{" "}
              By Platform
            </h1>
            <div className="text-[var(--text-color)] max-w-[750px] text-[16px] text-center lg:text-[18px] leading-relaxed mb-6">
              <p>
                Get reliable, instantly available, and monthly updated social
                demographics data on Facebook, Instagram, Messenger, and
                LinkedIn in one place.
              </p>
            </div>
            <SocialCounter
              totalRecords={Number(totalRecords)}
              totalPhoneNumbers={Number(totalPhoneNumbers)}
              totalEmails={Number(totalEmails)}
              totalFacebooks={Number(totalFacebooks)} // Example total Facebooks
              messages={messages}
              showRecords={true}
              showPhoneNumbers={true}
              showEmails={true}
              showFacebook={true} // Show the Facebook section
              icons={page1Icons}
              recordLink={`/social-stats/${countryName
                .toLowerCase()
                .replace(/\s+/g, "-")}/facebook-users-in-august-2024`}
              phoneNumberLink={`/social-stats/${countryName
                .toLowerCase()
                .replace(/\s+/g, "-")}/instagram-users-in-august-2024`}
              emailLink={`/social-stats/${countryName
                .toLowerCase()
                .replace(/\s+/g, "-")}/messenger-users-in-august-2024`}
              facebookLink={`/social-stats/${countryName
                .toLowerCase()
                .replace(/\s+/g, "-")}/linkedin-users-in-august-2024`}
            />
          </div>
        </div>
      </section>
      <div className="flex flex-col mb-4 items-center z-10 mt-16 p-4">
        <div className="max-w-screen-lg p-4 w-full rounded-lg">
          {/* Add the header section */}
          <div className="flex flex-col justify-center mb-4">
            <div className="tracking-wide text-[16px] uppercase text-[var(--text-color)] font-semibold mb-2">
              <span className="gradient-new-text">Social-Media Stats</span>
            </div>
            <h2 className="text-2xl lg:text-4xl font-semibold mb-2 tracking-wide text-[var(--title-color)]">
              Monthly Updated{" "}
              <span className="gradient-new-text">Social Media Data</span>
            </h2>
            <div className="text-[var(--text-color)] text-[16px] lg:text-[16px] leading-relaxed">
              <p>
                Access regularly updated social media data on top social media
                platforms for free. Unlock demographics data at zero costs and
                make informed decisions.
              </p>
            </div>
          </div>
          <div>
            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search by Month or Year..."
                className="border border-gray-300 rounded-lg p-2 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <table className="w-full h-[300px]">
              <tbody>
                <tr>
                  <td className="py-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Render filtered months */}
                      {filteredMonths.map(({ month, year }) => (
                        <Link
                          href={`/stats/${countryName
                            .toLowerCase()
                            .replace(
                              /\s+/g,
                              "-"
                            )}/social-media-users-in-${monthNames[
                            parseInt(month.split("/")[1], 10) - 1
                          ].toLowerCase()}-${year}`}
                          key={`${month}-${year}`}
                        >
                          <div className="border flex items-center gap-2 border-gray-300 rounded-lg shadow-md p-4 hover:bg-gray-100 transition duration-300 ease-in-out">
                            <div className="text-md flex-grow">
                              {
                                monthNames[
                                  parseInt(month.split("/")[1], 10) - 1
                                ]
                              }{" "}
                              {year}
                            </div>
                            <svg
                              width="20px"
                              height="20px"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4 12H20M20 12L14 6M20 12L14 18"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>{" "}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatsDetailPage;
